import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { Menu, Plus, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { selectBtgptFilesAutoRecognition, selectKeepWhatsappAwake } from '@common-reducers/UserSelectors';
import { setBtgptFilesAutoRecognition, setKeepWhatsappAwake } from '@common-reducers/UserThunks';
import { uploadAiChatAttachment } from '@common/services/ai-chat-upload';
import { useIsMobile } from '@common/hooks/use-is-mobile';
import { selectEngineState } from '@common/components/connected-engines/connected-engines.selectors';
import { engineStatusHint, engineStatusLabel } from '@common/components/connected-engines/engine-status-copy';
import PickerDialog from '@common-components/tasks/picker-dialog/picker-dialog-box';
import {
  AI_SWITCH_ON_CLASS,
  AiChat,
  AiChatHandle,
  AiChatItem,
  AiStreamError,
  DEFAULT_ATTACHMENT_PROMPT,
  isHiddenTool,
  UploadedAttachment,
  useAiStream,
} from '@common-components/ai-chat';
import { Mascot } from '@common-components/mascot/Mascot';
import { BoardTasksMessageBlock, adaptBoardTasksResult, BoardTasksBlockData } from './btgptUi/board-tasks-message-block';
import { SettingsPickerBlockView, PermissionsPickerCard } from './btgptUi/settings-picker-block';
import { SettingDropdown } from './btgptUi/setting-dropdown';
import {
  BTGPTServerClient,
  DEFAULT_PERMISSIONS,
  BTGPTConversationSummary,
  BTGPTStreamChunk,
  BTGPTTranscriptMessage,
} from './services/btgpt-server-client';
import { waitForSavedReply } from './services/pending-reply';
import { Message, BtgptPermissions, BtgptModel, SettingKey, SettingsPickerBlock } from './types/btgpt-types';

/** Whether the debug flag is set. Gates the debug-only 'local' model option. */
function isBtDebug(): boolean {
  try {
    return localStorage.getItem('bt-debug') === 'true';
  } catch {
    /* localStorage unavailable — treat as not-debug */
  }
  return false;
}

/** The agent's explicit language-switch directive, stripped from the shown answer. */
const SET_LANGUAGE_DIRECTIVE = /\[set-language:\s*[a-z]{2,3}\s*\]/gi;

/**
 * M3 (final review) — an attachment-only send left the local user bubble
 * EMPTY (`content: ''`) until a reload, at which point the server's own
 * persisted text ("See the attached file(s).") showed up instead — a
 * visible flash/mismatch. Mirrors `useBuilderChat`'s identical fix: the
 * locally-appended bubble uses the same default prompt the backend
 * substitutes for an attachment-only turn, so local always equals persisted.
 */
export function resolveLocalUserMessageText(text: string, hasAttachments: boolean): string {
  return text || (hasAttachments ? DEFAULT_ATTACHMENT_PROMPT : '');
}

/** Rebuild the interactive board blocks for a saved assistant turn from its
 *  persisted tool results (the boards tool writes `{ tool, action, data }` rows).
 *  One block per board — the LAST renderable result for a board wins (matches
 *  the live accumulation, where list_items supersedes the create_board block). */
function buildSavedBoardBlocks(msg: BTGPTTranscriptMessage): BoardTasksBlockData[] {
  const byBoard = new Map<string, BoardTasksBlockData>();
  for (const r of msg.toolResults ?? []) {
    const block = adaptBoardTasksResult(r);
    if (block) byBoard.set(block.board.rawBoardId, block);
  }
  return [...byBoard.values()];
}

/** Map a saved transcript into the in-memory Message[] the chat view renders. */
function transcriptToMessages(saved: BTGPTTranscriptMessage[]): Message[] {
  return saved.map((m) => ({
    id: m._id,
    content: m.content,
    role: m.role === 'assistant' ? 'assistant' : 'user',
    type: 'text',
    ...(m.role === 'assistant'
      ? {
          tools: (m.toolCalls ?? []).map((c) => c.tool ?? '').filter((name) => !isHiddenTool(name)),
          boardBlocks: buildSavedBoardBlocks(m),
        }
      : m.attachments?.length
        ? { attachments: m.attachments }
        : {}),
  }));
}

/** Messages → the chat's items: each assistant turn is preceded by the tools it used. */
function messagesToItems(messages: Message[]): AiChatItem[] {
  return messages.flatMap((m): AiChatItem[] =>
    m.role === 'user'
      ? [{ id: m.id, kind: 'user', text: m.content, ...(m.attachments?.length ? { attachments: m.attachments } : {}) }]
      : [
          ...(m.tools ?? []).map(
            (name, i): AiChatItem => ({
              id: `${m.id}:tool${i}`,
              kind: 'tool',
              name,
            })
          ),
          {
            id: m.id,
            kind: 'assistant',
            text: m.content,
            error: m.type === 'error',
          },
        ]
  );
}

/** What a live turn collected besides its text: a settings picker. (Board blocks come from the saved turn.) */
interface LiveExtras {
  settingsPicker?: SettingsPickerBlock;
}

const BTGPTView: React.FC<{ onClose?: () => void }> = ({ onClose }) => {
  const { t, i18n } = useTranslation();
  const { id: routeConversationId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const isRtl = i18n.dir(i18n.resolvedLanguage || i18n.language) === 'rtl';
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  // A turn that outlived the live stream (`error` code 'pending'): its answer is still
  // being written and will be saved. The chat shows the soft working row meanwhile.
  const [pendingConvId, setPendingConvId] = useState<string | null>(null);
  const [agent] = useState(() => new BTGPTServerClient());
  const [conversations, setConversations] = useState<BTGPTConversationSummary[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Granular per-capability permissions (send/schedule/history/archive) —
  // whether the agent may act freely or must ask first, per capability.
  // Per-conversation: hydrated from the record on open, persisted via PATCH on change.
  const [permissions, setPermissions] = useState<BtgptPermissions>({
    ...DEFAULT_PERMISSIONS,
  });
  // "Files auto-recognition" lives on the USER DOC (redux/DB) because the
  // WhatsApp extension must read it too (it gates whether a file sent in a
  // linked AI chat is auto-forwarded to the agent). Synced over MQTT.
  const reduxDispatch = useAppDispatch();
  const filesAutoRecognition = useAppSelector(selectBtgptFilesAutoRecognition);
  const keepAwake = useAppSelector(selectKeepWhatsappAwake);
  const engine = useAppSelector(selectEngineState);
  const accessToken = useAppSelector((state) => state.UserReducer?.accessToken);
  const tokenRef = useRef(accessToken);
  tokenRef.current = accessToken;
  const upload = useCallback((file: File) => uploadAiChatAttachment(file, tokenRef.current), []);
  // Which AI model btgpt uses. Per-conversation; fresh default = sonnet.
  const [model, setModel] = useState<BtgptModel>('sonnet');
  // The conversation's language (ISO-639-1) — '' until the conversation locks one.
  const [language, setLanguage] = useState<string>('');
  const btDebug = isBtDebug();
  const chatRef = useRef<AiChatHandle | null>(null);
  // The conversation whose transcript is currently loaded into `messages`. Used
  // to skip a redundant transcript reload right after WE create + navigate to a
  // new conversation (its messages are already on screen from the live stream).
  const loadedConvIdRef = useRef<string | null>(null);
  const liveExtras = useRef<LiveExtras>({});

  const refreshConversations = useCallback(async () => {
    try {
      setConversations(await agent.listConversations());
    } catch {
      /* non-fatal */
    }
  }, [agent]);

  // Persist a settings change to the current conversation record (PATCH). A
  // no-op before the conversation exists (the values ride along as the create
  // seed on the first message).
  const patchSettingsToServer = useCallback(
    (patch: { model?: BtgptModel; permissions?: BtgptPermissions; language?: string }) => {
      const id = routeConversationId ?? loadedConvIdRef.current;
      if (id) {
        void agent.patchConversationSettings(id, patch);
      }
    },
    [agent, routeConversationId]
  );

  const persistPermissions = useCallback(
    (next: BtgptPermissions) => {
      setPermissions(next);
      patchSettingsToServer({ permissions: next });
    },
    [patchSettingsToServer]
  );

  const handlePermissionToggle = useCallback(
    (key: keyof BtgptPermissions, value: boolean) => {
      persistPermissions({ ...permissions, [key]: value });
    },
    [permissions, persistPermissions]
  );

  // Master "Allow all" toggle: ON sets all four true, OFF sets all four false.
  const handleAllowAllToggle = useCallback(
    (value: boolean) => {
      persistPermissions({
        send: value,
        schedule: value,
        history: value,
        archive: value,
      });
    },
    [persistPermissions]
  );

  const handleModelChange = useCallback(
    (value: BtgptModel) => {
      setModel(value);
      patchSettingsToServer({ model: value });
    },
    [patchSettingsToServer]
  );

  const handleLanguageChange = useCallback(
    (value: string) => {
      setLanguage(value);
      patchSettingsToServer({ language: value });
    },
    [patchSettingsToServer]
  );

  const appendMessage = useCallback((m: Omit<Message, 'id'>) => {
    setMessages((prev) => [...prev, { ...m, id: `${m.role}-${Date.now()}-${prev.length}` }]);
  }, []);

  const errorText = useCallback(
    (err: AiStreamError): string => {
      if (err.kind === 'http' && err.status === 401) return t('btgpt.chat.authRequired', 'Authentication required. Please log in again.');
      if (err.kind !== 'transport' && err.message) return err.message;
      return t('btgpt.chat.errorMessage');
    },
    [t]
  );

  // Always streamed: the chat's detail level ("Show while it works") only filters what it renders.
  const stream = useAiStream(undefined, {
    onChunk: (raw) => {
      const chunk = raw as BTGPTStreamChunk;
      if (chunk.type === 'error' && chunk.conversationId) pendingIdRef.current = chunk.conversationId;
      if (chunk.type === 'settings-picker' && chunk.setting) {
        // A bare /model /permissions — render the inline picker.
        liveExtras.current.settingsPicker = {
          setting: chunk.setting,
          current: chunk.current ?? '',
          options: chunk.options ?? [],
        };
      } else if (chunk.type === 'settings-changed') {
        // A /model opus-style direct set (or a WhatsApp-driven change).
        if (chunk.setting === 'model' && chunk.value) setModel(chunk.value as BtgptModel);
        else if (chunk.setting === 'permissions' && chunk.permissions) setPermissions(chunk.permissions);
        const picker = liveExtras.current.settingsPicker;
        if (picker)
          liveExtras.current.settingsPicker = {
            ...picker,
            chosen: chunk.value,
          };
      }
    },
    onDone: ({ text, tools, chunk }) => {
      // Busy ends with the answer, in the same render (AiChat paints a `done` turn while busy).
      setIsLoading(false);
      const { conversationId } = chunk as BTGPTStreamChunk;
      if (conversationId) {
        // Mark as loaded so the URL-sync effect doesn't reload (and wipe) the
        // messages we just streamed, then reflect the id in the URL.
        loadedConvIdRef.current = conversationId;
        if (!routeConversationId) navigate(`/btgpt/${conversationId}`, { replace: true });
      }
      const extras = liveExtras.current;
      appendMessage({
        role: 'assistant',
        type: 'text',
        content: text.replace(SET_LANGUAGE_DIRECTIVE, '').trim(),
        tools: tools.map((x) => x.name),
        settingsPicker: extras.settingsPicker,
      });
      // Refresh the sidebar so a new conversation / updated title appears.
      refreshConversations();
    },
    // The Stop button, pressed on ANY tab watching this conversation: a terminal
    // `stopped` chunk (no `done`/`error`). Our OWN Stop click (`stopBtgpt` below)
    // already aborts this fetch locally, so in practice this fires only for a
    // stop issued elsewhere while this tab still streams the same turn.
    onStopped: ({ text, chunk }) => {
      setIsLoading(false);
      const { conversationId } = chunk as BTGPTStreamChunk;
      if (conversationId) {
        loadedConvIdRef.current = conversationId;
        if (!routeConversationId) navigate(`/btgpt/${conversationId}`, { replace: true });
      }
      appendMessage({ role: 'assistant', type: 'text', content: text || t('btgpt.chat.stopped', 'Stopped.') });
      refreshConversations();
    },
    // Keep a partial answer if one arrived, with the error under it (the
    // backend resets the text itself when the turn is still pending).
    onError: (err, partial) => {
      const pendingId = err.code === 'pending' ? pendingIdRef.current : null;
      if (pendingId) {
        // Still working: not an error. Adopt the conversation, then wait for the saved answer.
        // The stream stays in `pending` (the status line says so) until the wait ends.
        loadedConvIdRef.current = pendingId;
        if (!routeConversationId) navigate(`/btgpt/${pendingId}`, { replace: true });
        refreshConversations();
        setPendingConvId(pendingId);
        return;
      }
      if (partial.trim()) {
        appendMessage({ role: 'assistant', type: 'text', content: partial.replace(SET_LANGUAGE_DIRECTIVE, '').trim() });
      }
      appendMessage({ role: 'assistant', type: 'error', content: errorText(err) });
    },
  });

  // The conversation id an `error` chunk named (btgpt sends it with code 'pending').
  const pendingIdRef = useRef<string | null>(null);

  // Pending turn: re-read the saved transcript until the answer lands (bounded).
  useEffect(() => {
    if (!pendingConvId) return;
    const ctrl = new AbortController();
    waitForSavedReply(async () => (await agent.loadTranscript(pendingConvId)).messages, ctrl.signal).then((saved) => {
      if (ctrl.signal.aborted) return;
      setPendingConvId(null);
      if (saved) {
        loadedConvIdRef.current = pendingConvId;
        setMessages(transcriptToMessages(saved));
        refreshConversations();
      } else {
        // A soft note, not an error: the answer is still coming, just later.
        appendMessage({
          role: 'assistant',
          type: 'text',
          content: t(
            'btgpt.chat.stillPending',
            'This is taking longer than usual. The answer will appear in this conversation when it is ready — reopen it in a few minutes.'
          ),
        });
      }
    });
    return () => ctrl.abort();
  }, [pendingConvId, agent, refreshConversations, appendMessage, t]);

  // Leaving the pending conversation any way at all (New chat, another row,
  // Back/Forward, a URL edit) stops the wait and unlocks the composer.
  useEffect(() => {
    setPendingConvId((p) => (p && p !== routeConversationId ? null : p));
  }, [routeConversationId]);

  // The wait is over (the saved answer landed, or it was abandoned): the live turn goes.
  const streamPhase = stream.phase;
  const resetStream = stream.reset;
  useEffect(() => {
    if (!pendingConvId && streamPhase === 'pending') resetStream();
  }, [pendingConvId, streamPhase, resetStream]);

  // Load the conversations list on mount.
  useEffect(() => {
    refreshConversations();
  }, [refreshConversations]);

  // Sync the view to the URL: when `/btgpt/:id` changes, load that
  // conversation's saved transcript. No id → the new-chat screen. Skip the
  // reload right after we create + navigate to a fresh conversation.
  useEffect(() => {
    let cancelled = false;
    if (!routeConversationId) {
      loadedConvIdRef.current = null;
      setMessages([]);
      return;
    }
    if (loadedConvIdRef.current === routeConversationId) return;
    agent.loadTranscript(routeConversationId).then(({ messages: saved }) => {
      if (cancelled) return;
      loadedConvIdRef.current = routeConversationId;
      setMessages(transcriptToMessages(saved));
    });
    // Hydrate the settings from this conversation's record (only on explicit
    // open — "New Chat" inherits the config of the conversation you were viewing).
    agent.getConversationSettings(routeConversationId).then((s) => {
      if (cancelled || !s) return;
      if (s.model) setModel(s.model);
      if (s.permissions) setPermissions(s.permissions);
      setLanguage(s.language ?? '');
    });
    return () => {
      cancelled = true;
    };
  }, [routeConversationId, agent]);

  // Apply a settings picker choice: update the control, persist, mark the picker resolved.
  const applySettingChoice = useCallback(
    (setting: SettingKey, option: string) => {
      if (setting === 'model') {
        setModel(option as BtgptModel);
        patchSettingsToServer({ model: option as BtgptModel });
      } else {
        const perms: BtgptPermissions =
          option === 'allow-all'
            ? { send: true, schedule: true, history: true, archive: true }
            : { send: false, schedule: true, history: true, archive: true };
        setPermissions(perms);
        patchSettingsToServer({ permissions: perms });
      }
      setMessages((prev) =>
        prev.map((m) =>
          m.settingsPicker && m.settingsPicker.setting === setting && !m.settingsPicker.chosen
            ? { ...m, settingsPicker: { ...m.settingsPicker, chosen: option } }
            : m
        )
      );
    },
    [patchSettingsToServer]
  );

  const sendText = useCallback(
    async (text: string, attachments?: UploadedAttachment[]) => {
      const hasAttachments = !!attachments && attachments.length > 0;
      if ((!text.trim() && !hasAttachments) || isLoading) return;
      appendMessage({
        role: 'user',
        type: 'text',
        content: resolveLocalUserMessageText(text, hasAttachments),
        ...(hasAttachments ? { attachments: attachments!.map((a) => ({ name: a.name, mime: a.mime })) } : {}),
      });
      setIsLoading(true);
      liveExtras.current = {};
      pendingIdRef.current = null;
      const conversationId = routeConversationId ?? null;
      const ids = hasAttachments ? attachments!.map((a) => a.uploadId) : undefined;
      try {
        await stream.start((signal) => agent.openStream(text, conversationId, { permissions, model }, signal, ids));
      } finally {
        setIsLoading(false);
      }
    },
    [isLoading, appendMessage, routeConversationId, stream, agent, permissions, model]
  );

  // Stop button: abort the live SSE/fetch (or the pending-reply wait), tell the
  // server, and leave a neutral "Stopped" row — never an error.
  const stopBtgpt = useCallback(async () => {
    if (!isLoading && !pendingConvId) return;
    const id = pendingConvId ?? routeConversationId ?? loadedConvIdRef.current;
    stream.reset();
    setIsLoading(false);
    setPendingConvId(null);
    if (id) {
      void agent.stopConversation(id);
    }
    appendMessage({ role: 'assistant', type: 'text', content: t('btgpt.chat.stopped', 'Stopped.') });
  }, [isLoading, pendingConvId, stream, routeConversationId, agent, appendMessage, t]);

  const handleNewChat = () => {
    stream.reset();
    setMessages([]);
    chatRef.current?.setDraft('');
    loadedConvIdRef.current = null;
    setDrawerOpen(false);
    if (routeConversationId) navigate('/btgpt');
    chatRef.current?.focus();
  };

  const handleSelectConversation = (convId: string) => {
    setDrawerOpen(false);
    if (convId === routeConversationId) return;
    navigate(`/btgpt/${convId}`);
  };

  // The mobile conversation drawer: focus moves in on open and back to its
  // button on close; Escape closes it.
  const drawerRef = useRef<HTMLElement | null>(null);
  const drawerButtonRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    if (!drawerOpen) return;
    drawerRef.current?.querySelector<HTMLElement>('button')?.focus();
    const opener = drawerButtonRef.current;
    return () => opener?.focus();
  }, [drawerOpen]);
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDrawerOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerOpen]);
  useEffect(() => {
    if (!isMobile) setDrawerOpen(false);
  }, [isMobile]);

  const items = useMemo(() => messagesToItems(messages), [messages]);
  const byId = useMemo(() => new Map(messages.map((m) => [m.id, m])), [messages]);

  // Board blocks + settings pickers under an assistant turn (btgpt-only widgets).
  const renderExtra = useCallback(
    (item: AiChatItem) => {
      if (item.kind !== 'assistant') return null;
      const m = byId.get(item.id);
      if (!m) return null;
      return (
        <>
          {m.boardBlocks?.map((block, i) => (
            <BoardTasksMessageBlock key={`${block.board.rawBoardId}-${i}`} board={block.board} items={block.items} action={block.action} />
          ))}
          {m.settingsPicker &&
            (m.settingsPicker.setting === 'permissions' ? (
              <PermissionsPickerCard permissions={permissions} onToggle={handlePermissionToggle} onToggleAll={handleAllowAllToggle} />
            ) : (
              <SettingsPickerBlockView block={m.settingsPicker} onChoose={applySettingChoice} />
            ))}
        </>
      );
    },
    [byId, permissions, handlePermissionToggle, handleAllowAllToggle, applySettingChoice]
  );

  const currentTitle = conversations.find((c) => c._id === routeConversationId)?.title;

  const conversationList = (
    <div className="flex h-full flex-col" data-testid="btgpt-conversations">
      <div className="p-3">
        <Button type="button" variant="ai" onClick={handleNewChat} className="h-10 w-full">
          <Plus />
          {t('btgpt.newChat', 'New Chat')}
        </Button>
      </div>
      <nav className="flex-1 overflow-y-auto px-2 pb-3">
        {conversations.length === 0 && (
          <div className="px-3 py-2 text-xs text-ai-text-faint">{t('btgpt.noConversations', 'No conversations yet')}</div>
        )}
        {conversations.map((c) => (
          <button
            key={c._id}
            type="button"
            onClick={() => handleSelectConversation(c._id)}
            aria-current={c._id === routeConversationId ? 'page' : undefined}
            className={cn(
              'flex w-full items-center gap-2 rounded-ai-sm px-3 py-2 text-start text-sm transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ai-focus-ring',
              c._id === routeConversationId
                ? 'bg-ai-muted font-medium text-ai-text'
                : 'text-ai-text-secondary hover:bg-ai-muted hover:text-ai-text'
            )}
          >
            <span dir="auto" className="truncate">
              {c.title || t('btgpt.untitled', 'New conversation')}
            </span>
            {c.waChatId && (
              // WhatsApp-green dot — this conversation is bridged to a WhatsApp "AI Conversation" group.
              <span
                className="ms-auto h-2 w-2 shrink-0 rounded-full bg-[var(--wa-unread-badge)]"
                aria-label={t('btgpt.linkedToWhatsapp', 'Linked to WhatsApp')}
              />
            )}
          </button>
        ))}
      </nav>
    </div>
  );

  const header = (
    <>
      {isMobile && (
        <button
          type="button"
          ref={drawerButtonRef}
          onClick={() => setDrawerOpen(true)}
          aria-label={t('btgpt.conversations', 'Conversations')}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-ai-text-secondary hover:bg-ai-muted hover:text-ai-text outline-none focus-visible:ring-2 focus-visible:ring-ai-focus-ring"
          data-testid="btgpt-drawer-open"
        >
          <Menu className="h-5 w-5" />
        </button>
      )}
      <Mascot size={26} />
      <span dir="auto" className="min-w-0 truncate text-[15px] font-semibold tracking-tight text-ai-text">
        {currentTitle || t('btgpt.pageTitle')}
      </span>
      <div className="ms-auto flex shrink-0 items-center gap-1.5">
        {/* Model picker: per-conversation; the /model command is shown inside the panel. */}
        <SettingDropdown
          title={t('btgpt.model.title', 'Model')}
          command="/model"
          value={model}
          options={[
            { value: 'haiku', label: 'Haiku' },
            { value: 'sonnet', label: 'Sonnet' },
            { value: 'opus', label: 'Opus' },
            { value: 'fable', label: 'Fable' },
            ...(btDebug ? [{ value: 'local', label: 'Local' }] : []),
          ]}
          onChange={(v) => handleModelChange(v as BtgptModel)}
        />
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label={t('btgpt.close', 'Close')}
            className="flex h-9 w-9 items-center justify-center rounded-full text-ai-text-secondary hover:bg-ai-muted hover:text-ai-text outline-none focus-visible:ring-2 focus-visible:ring-ai-focus-ring"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>
    </>
  );

  // The chat `⋯` menu: permissions, language, WhatsApp connection (under "Show while it works").
  const menu = (
    <>
      <div className="border-t border-ai-border pt-3">
        <PermissionsPickerCard
          bare
          showHints
          permissions={permissions}
          onToggle={handlePermissionToggle}
          onToggleAll={handleAllowAllToggle}
        >
          {/* Files auto-recognition — user-doc-backed (NOT localStorage): the
              WhatsApp extension reads it to gate the linked-AI-chat file flow. */}
          <label className="mt-1 flex cursor-pointer items-start justify-between gap-3 border-t border-ai-border pt-2">
            <span className="flex flex-col gap-0.5">
              <span className="text-xs text-ai-text">{t('btgpt.permissions.filesAuto', 'Files auto-recognition')}</span>
              <span className="text-[11px] leading-snug text-ai-text-secondary">
                {t(
                  'btgpt.permissions.filesAutoHint',
                  'On: a file you send in an AI chat is identified automatically — invoices are filed to your Invoices board, anything else the agent asks about. Off: files are ignored unless you reply on them or ask.'
                )}
              </span>
            </span>
            <Switch
              className={`mt-0.5 shrink-0 ${AI_SWITCH_ON_CLASS}`}
              checked={filesAutoRecognition}
              onCheckedChange={(checked) => reduxDispatch(setBtgptFilesAutoRecognition(checked))}
              aria-label={t('btgpt.permissions.filesAuto', 'Files auto-recognition')}
            />
          </label>
        </PermissionsPickerCard>
      </div>
      <div className="flex items-center justify-between gap-3 border-t border-ai-border pt-3">
        <span className="text-xs font-semibold text-ai-text">{t('btgpt.language.title', 'Language')}</span>
        {/* The reply language + the voice transcription hint. Auto-locks to the
            first message; picking one here is an explicit override. */}
        <SettingDropdown
          title={t('btgpt.language.title', 'Language')}
          value={language}
          options={[
            { value: 'he', label: 'עברית' },
            { value: 'en', label: 'English' },
            { value: 'ar', label: 'العربية' },
            { value: 'es', label: 'Español' },
            { value: 'fr', label: 'Français' },
            { value: 'de', label: 'Deutsch' },
            { value: 'it', label: 'Italiano' },
            { value: 'pt', label: 'Português' },
            { value: 'hi', label: 'हिन्दी' },
          ]}
          onChange={handleLanguageChange}
        />
      </div>
      <div className="flex flex-col gap-2 border-t border-ai-border pt-3" data-testid="btgpt-connection">
        <span className="text-xs font-semibold text-ai-text">{t('btgpt.connection', 'WhatsApp connection')}</span>
        {/* One plain sentence: the state and what it means (same words as the header chip). */}
        <p dir="auto" className="flex items-start gap-2 text-xs leading-snug text-ai-text-secondary">
          <span className={cn('mt-1 h-2 w-2 shrink-0 rounded-full', engine.status.connected ? 'bg-ai-accent' : 'bg-ai-text-faint')} />
          <span>
            <span className="font-medium text-ai-text">{engineStatusLabel(t, engine.status.text)}</span>
            {engineStatusHint(t, engine.status.text) && <> · {engineStatusHint(t, engine.status.text)}</>}
          </span>
        </p>
        {/* Keep-awake, labelled: what the switch turns on is written next to it. */}
        <label className="flex cursor-pointer items-start justify-between gap-3">
          <span className="flex flex-col gap-0.5">
            <span className="text-xs text-ai-text">{t('status.hint.keepAwake.title', 'Keep WhatsApp awake')}</span>
            <span className="text-[11px] leading-snug text-ai-text-secondary">
              {keepAwake
                ? t(
                    'status.hint.keepAwake.on',
                    "On — we gently wake your WhatsApp tab so Chrome doesn't put it to sleep and your messages keep sending."
                  )
                : t(
                    'status.hint.keepAwake.off',
                    'Off — if WhatsApp sits in a background tab, Chrome may put it to sleep and messages can be delayed.'
                  )}
            </span>
          </span>
          <Switch
            className={`mt-0.5 shrink-0 ${AI_SWITCH_ON_CLASS}`}
            checked={keepAwake}
            onCheckedChange={(checked) => reduxDispatch(setKeepWhatsappAwake(checked))}
            aria-label={t('status.hint.keepAwake.title', 'Keep WhatsApp awake')}
          />
        </label>
      </div>
    </>
  );

  return (
    <div className="flex h-dvh min-h-0 bg-ai-bg text-ai-text" dir={isRtl ? 'rtl' : 'ltr'} data-testid="btgpt">
      {!isMobile && <aside className="w-64 shrink-0 border-e border-ai-border bg-ai-surface">{conversationList}</aside>}

      <div className="flex min-w-0 flex-1 flex-col">
        <AiChat
          ref={chatRef}
          items={items}
          onSend={(text, attachments) => void sendText(text, attachments)}
          attachments={{ upload }}
          busy={isLoading || pendingConvId !== null}
          onStop={stopBtgpt}
          stream={stream}
          header={header}
          menu={menu}
          empty={
            <div className="flex flex-col items-center gap-3 text-center">
              <Mascot size={72} glow level="full" />
              <h1 className="mt-2 text-[28px] font-semibold leading-tight tracking-[-0.03em] text-ai-text sm:text-[32px]">
                {t('btgpt.pageTitle')}
              </h1>
              <p className="text-ai-text-secondary">{t('btgpt.emptyPrompt')}</p>
            </div>
          }
          suggestions={[
            t('btgpt.exampleQueries.whatsappChats'),
            t('btgpt.exampleQueries.contacts'),
            t('btgpt.exampleQueries.scheduledMessages'),
            t('btgpt.exampleQueries.phoneNumbers'),
          ]}
          renderExtra={renderExtra}
          composer={{}}
          dir={isRtl ? 'rtl' : 'ltr'}
          cornerClearance
        />
      </div>

      {isMobile && drawerOpen && (
        <div className="fixed inset-0 z-[1400] flex" role="dialog" aria-modal="true" aria-label={t('btgpt.conversations', 'Conversations')}>
          <div className="absolute inset-0 bg-black/40 animate-in fade-in" onClick={() => setDrawerOpen(false)} />
          <aside
            ref={drawerRef}
            className="relative h-full w-[82%] max-w-[300px] border-e border-ai-border bg-ai-surface shadow-2xl animate-in slide-in-from-left rtl:slide-in-from-right duration-(--ai-duration) ease-ai"
          >
            {conversationList}
          </aside>
        </div>
      )}

      {/* Picker host for the interactive board blocks. PickerDialog is normally
          mounted per board page; the /btgpt route has no board page, so we mount
          one shared instance here. It renders nothing until a board cell opens a
          picker (reads PickerDialogReducer from the shared web store). */}
      <PickerDialog boardId="" />
    </div>
  );
};

export { BTGPTView };
export default BTGPTView;
